/**
 * Strictly necessary cart utilities.
 * Loaded as an external Shopify asset so OneTrust OtAutoBlock does not disable it on reject-all.
 */
(function () {
  window.utils = window.utils || {};

  if (window.utils.cart && typeof window.utils.cart.addProduct === "function") {
    return;
  }

  const cart = {
    ensureMcUserCache: () => {
      try {
        const raw = sessionStorage.getItem("mcUser");
        if (raw) {
          const userData = JSON.parse(raw);
          if (userData && typeof userData === "object") {
            if (!userData.cart) userData.cart = { items: [], item_count: 0 };
            if (!Array.isArray(userData.cart.items)) userData.cart.items = [];
            return userData;
          }
        }
      } catch (error) {
        console.warn("mcUser cache read skipped:", error);
      }
      return { cart: { items: [], item_count: 0 } };
    },
    syncMcUserFromCart: (cartData) => {
      try {
        const userData = cart.ensureMcUserCache();
        userData.cart = cartData;
        sessionStorage.setItem("mcUser", JSON.stringify(userData));
      } catch (error) {
        console.warn("mcUser cart sync skipped:", error);
      }
    },
    purgeInvalidMcUser: () => {
      try {
        const raw = sessionStorage.getItem("mcUser");
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") {
          sessionStorage.removeItem("mcUser");
        }
      } catch (error) {
        sessionStorage.removeItem("mcUser");
      }
    },
    normalizeAddToCartPayload: (cartData, quantity = 1) => {
      if (
        cartData &&
        typeof cartData === "object" &&
        Array.isArray(cartData.items) &&
        cartData.items.length > 0
      ) {
        return cartData;
      }
      const variantId = cartData != null && cartData !== "" ? String(cartData) : "";
      if (!variantId) {
        throw new Error("Invalid add-to-cart payload: missing variant id");
      }
      const item = {
        quantity: Math.max(1, parseInt(quantity, 10) || 1),
        id: variantId,
        properties: {},
      };
      if (cartData.selling_plan) {
        item.selling_plan = cartData.selling_plan;
      }
      return { items: [item] };
    },
    check_data: () => {
      cart.purgeInvalidMcUser();
      cart.updateCount().catch((error) => {
        console.warn("Cart count check skipped:", error);
      });
    },
    addProduct: (cartData, productPrice) => {
      let payload;
      try {
        payload = cart.normalizeAddToCartPayload(cartData);
      } catch (error) {
        console.error("Error Adding To Cart", error);
        return Promise.reject(error);
      }
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then(async (response) => {
          const data = await response.json();
          if (!response.ok) {
            const message =
              data.description || data.message || `Add to cart failed (${response.status})`;
            throw new Error(message);
          }
          return data;
        })
        .then(async () => {
          try {
            if (typeof openCartDrawer === "function") {
              openCartDrawer(true);
            } else if (typeof reloadCartDrawer === "function") {
              reloadCartDrawer();
            }
          } catch (drawerError) {
            console.warn("Cart drawer refresh skipped:", drawerError);
          }

          let fullCart;
          try {
            const cartResponse = await fetch("/cart.js", {
              method: "GET",
              headers: { Accept: "application/json" },
              cache: "no-store",
            });
            fullCart = await cartResponse.json();
          } catch (fetchError) {
            console.warn("Full cart fetch skipped after add:", fetchError);
            fullCart = null;
          }

          if (fullCart) {
            cart.syncMcUserFromCart(fullCart);
            cart.updateStaticCounts(fullCart.item_count);
          }

          try {
            if (fullCart) {
              document.dispatchEvent(
                new CustomEvent("cellcore:cart-updated", {
                  detail: { source: "cart-core-add", cart: fullCart },
                })
              );
            }
          } catch (eventError) {
            console.warn("Cart updated event skipped:", eventError);
          }

          try {
            if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) {
              window.dataLayer?.push?.({
                event: "add_to_cart",
                ecommerce: { items: fullCart?.items || [] },
              });
            }
          } catch (trackingError) {
            console.warn("Add-to-cart tracking skipped:", trackingError);
          }

          return fullCart?.items?.[fullCart.items.length - 1] || fullCart;
        })
        .catch((error) => {
          console.error("Error Adding To Cart", error);
          throw error;
        });
    },
    updateProduct: (variantID, quantity) => {
      const variant_id = variantID.toString();
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity: quantity, id: variant_id }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Cart update failed (${response.status})`);
          }
          return response.json();
        })
        .then(async (data) => {
          cart.syncMcUserFromCart(data);
          cart.updateStaticCounts(data.item_count);
          try {
            if (typeof openCartDrawer === "function") {
              openCartDrawer(true);
            } else if (typeof reloadCartDrawer === "function") {
              reloadCartDrawer();
            }
          } catch (drawerError) {
            console.warn("Cart drawer refresh skipped:", drawerError);
          }
          try {
            document.dispatchEvent(
              new CustomEvent("cellcore:cart-updated", {
                detail: { source: "cart-core-update", cart: data },
              })
            );
          } catch (eventError) {
            console.warn("Cart updated event skipped:", eventError);
          }
          return data;
        })
        .catch((error) => {
          console.error("Error Updating Cart", error);
          throw error;
        });
    },
    updateCount: () => {
      return fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      })
        .then((response) => response.json())
        .then((data) => {
          cart.syncMcUserFromCart(data);
          cart.updateStaticCounts(data.item_count);
          return data;
        });
    },
    updateCountManually: (variantID, quantity, added, productPrice) => {
      try {
        const userData = cart.ensureMcUserCache();
        if (added) {
          userData.cart.items.push({ quantity: 1, variant_id: variantID, price: productPrice });
        } else {
          for (let i = 0; i < userData.cart.items.length; i++) {
            const item = userData.cart.items[i];
            if (item.variant_id == variantID) {
              if (quantity == 0) {
                userData.cart.items.splice(i, 1);
              } else {
                item.quantity = quantity;
              }
              break;
            }
          }
        }
        userData.cart.item_count = userData.cart.items.reduce(
          (total, item) => total + item.quantity,
          0
        );
        sessionStorage.setItem("mcUser", JSON.stringify(userData));
        cart.updateStaticCounts(userData.cart.item_count);
      } catch (error) {
        console.warn("mcUser manual count update skipped:", error);
      }
    },
    updateStaticCounts: (count) => {
      const total = Number(count);
      if (!Number.isFinite(total) || total < 0) return;
      const display = total > 99 ? "99+" : String(total);
      const label = total === 1 ? "1 item" : `${display} items`;

      // Dawn-style bubbles: floating sidecart and any classic header cart.
      // Nav icons use .cc-nav__cart-count and are painted below.
      document.querySelectorAll("a.header__icon--cart, #cart-icon-float").forEach((host) => {
        if (host.classList.contains("cc-nav__cart")) return;
        let bubble = host.querySelector(".cart-count-bubble");
        if (total === 0) {
          bubble?.remove();
          return;
        }
        if (!bubble) {
          bubble = document.createElement("div");
          bubble.className = "cart-count-bubble";
          host.appendChild(bubble);
        }
        let visible = bubble.querySelector("[aria-hidden='true']");
        let hidden = bubble.querySelector(".visually-hidden");
        if (!visible || !hidden) {
          bubble.replaceChildren();
          visible = document.createElement("span");
          visible.setAttribute("aria-hidden", "true");
          hidden = document.createElement("span");
          hidden.className = "visually-hidden";
          bubble.append(visible, hidden);
        }
        visible.textContent = display;
        hidden.textContent = label;
      });

      document.querySelectorAll("a.cc-nav__cart").forEach((icon) => {
        icon.querySelectorAll(".cart-count-bubble").forEach((node) => node.remove());
        let badge = icon.querySelector(".cc-nav__cart-count");
        let announcement = icon.querySelector("[data-nav-cart-count]");
        if (total === 0) {
          badge?.remove();
          announcement?.remove();
          return;
        }
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "cc-nav__cart-count";
          badge.setAttribute("aria-hidden", "true");
          icon.appendChild(badge);
        }
        badge.textContent = display;
        if (!announcement) {
          announcement = document.createElement("span");
          announcement.className = "visually-hidden";
          announcement.setAttribute("data-nav-cart-count", "");
          icon.appendChild(announcement);
        }
        announcement.textContent = label;
      });
    },
    dropFullCart: () => {
      return fetch("/cart/clear.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })
        .then((response) => response.json())
        .then((data) => {
          cart.updateStaticCounts(0);
          return data;
        })
        .catch((error) => {
          console.error("Error Clearing Cart", error);
        });
    },
  };

  window.utils.cart = cart;
})();
